// @flow

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import { route } from "@routes/helper";
import { GSGrid, GSRenderers, Button } from "@components";
import { formatDate } from "@transformers/date";
import type { NotificationContact } from "../../reducer/hierarchy/types";

type OwnProps = $Exact<{
  notifications: NotificationContact[],
  deleteHierarchyNotification: (notificationIds: number[]) => void,
  toggleModal: (
    $Exact<{
      visible: boolean,
      notificationContact?: NotificationContact,
    }>,
  ) => () => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  selected: number[],
}>;

export class NotificationContactsTable extends React.Component<Props, ComponentState> {
  state = {
    selected: [],
  };
  onToggleAll = () => {
    this.state.selected.length === this.allowedDataItems().length
      ? this.deselectAllHandler()
      : this.onSelectAll();
  };
  onSelect = (notificationId: number) => (val: string) => {
    this.setState({
      ...this.state,
      selected: val,
    });
  };
  onSelectAll = () =>
    this.setState({
      selected: this.allowedDataItems().map(itm => itm.id),
    });

  allowedDataItems = () =>
    this.props.notifications.filter(notification => !notification.isInherited);

  deselectAllHandler = () =>
    this.setState({
      selected: [],
    });

  onDeleteHierarchyNotification = (notificationIds: number[]) => () => {
    this.deselectAllHandler();
    this.props.deleteHierarchyNotification(notificationIds);
  };

  getDepartmentScope = (item: NotificationContact) => {
    if (!item.isInherited && !item.isCascading) {
      return this.props.t("THIS_DEPARTMENT_ONLY");
    }

    if (!item.isInherited && item.isCascading) {
      return this.props.t("THIS_DEPARTMENT_AND_CHILDREN");
    }

    if (item.isInherited) {
      return `${this.props.t("INHERITED_FROM")} ${item.inheritedFrom.name}`;
    }

    return "";
  };

  get columns() {
    return [
      {
        key: "employeeName",
        name: this.props.t("NAME"),
        align: "left",
        renderCell: ({ row }: { row: NotificationContact }) => (
          <a
            href={route("REPORTING_EMPLOYEE_DETAILS_PATH", {
              clientId: row.organisationalUnit.clientId,
              employeeId: row.recipient.id,
            })}
            target="_blank"
            rel="noopener noreferrer"
            className="text-color-coffee-d1 ellipsis"
          >
            {row.recipient.name}
          </a>
        ),
      },
      {
        key: "departmentScope",
        name: this.props.t("SCOPE"),
        align: "left",
        renderCell: ({ row }: { row: NotificationContact }) => (
          <div className="ellipsis">{this.getDepartmentScope(row)}</div>
        ),
      },
      {
        key: "notificationType",
        name: this.props.t("TYPE"),
        renderCell: ({ row }: { row: NotificationContact }) => this.props.t(row.notificationType),
      },
      {
        key: "dei",
        align: "center",
        name: this.props.t("DEI"),
        renderCell: ({ row }: { row: NotificationContact }) => {
          if (row.notificationType === "BASIC_NOTIFICATION") {
            return <div className="circle font-weight-bold bg-mint">L</div>;
          }
          return row.notificationType === "STANDARD_NOTIFICATION" ? (
            <div className="circle font-weight-bold bg-coffee">M</div>
          ) : (
            <div className="circle font-weight-bold bg-rose">H</div>
          );
        },
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_ADDED"),
        renderCell: ({ row }: { row: NotificationContact }) => formatDate(row.createdAt),
      },
      {
        key: "createdBy",
        name: this.props.t("ADDED_BY"),
        renderCell: ({ row }: { row: NotificationContact }) =>
          GSRenderers.updatedBy({ ...row, t: this.props.t }),
      },
      {
        key: "edit",
        width: 50,
        align: "center",
        name: this.props.t("EDIT"),
        renderCell: ({ row }: { row: NotificationContact }) =>
          !row.isInherited && (
            <span
              className="material-icons pointer font-size-26"
              onClick={this.props.toggleModal({ visible: true, notificationContact: row })}
            >
              edit
            </span>
          ),
      },
      {
        key: "delete",
        width: 50,
        name: this.props.t("DELETE"),
        align: "center",
        itemClasskey: "justify-content-center",
        renderCell: ({ row }: { row: NotificationContact }) =>
          !row.isInherited && (
            <div className="d-flex" onClick={e => e.stopPropagation()}>
              <span
                className="material-icons pointer font-size-26"
                onClick={this.onDeleteHierarchyNotification([row.id])}
              >
                delete
              </span>
            </div>
          ),
      },
    ];
  }
  handleOnSelectRowsChange = (selectedRows: Set<number>) => {
    this.setState({
      selected: selectedRows,
    });
  };
  render() {
    return (
      <>
        <GSGrid
          rows={this.props.notifications}
          selectedRows={this.state.selected}
          onSelectedRowsChange={this.handleOnSelectRowsChange}
          columns={this.columns}
        />
        {!!this.state.selected.length && (
          <div className="d-flex align-items-center mt-3">
            <span className="pr-3 text-orange">
              {this.props.t(["COUNT_RECORD_SELECTED", "COUNT_RECORD_SELECTED_plural", "count"], {
                count: this.state.selected.length,
              })}
            </span>
            <Button
              size="small"
              color="negative"
              onClick={this.onDeleteHierarchyNotification(this.state.selected)}
            >
              {this.props.t("DELETE")}
            </Button>
            <Button size="small" color="secondary" onClick={this.deselectAllHandler}>
              {this.props.t("DESELECT_ALL")}
            </Button>
          </div>
        )}
      </>
    );
  }
}

export default localize()(NotificationContactsTable);
